<template>
  <div class="operations-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER ROW ==================== -->
    <div class="op-header-row mb-6">
      <div>
        <div class="d-flex align-center gap-3">
          <h1 class="op-main-title">وسائل النقل</h1>
        </div>
        <p class="op-subtitle">إدارة المركبات والسائقين وتتبع حالة الترخيص والتخصيص التشغيلي</p>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex align-center gap-2">
        <button type="button" class="op-btn-primary" @click="openAddVehicleModal">
          <v-icon size="16" color="#FFFFFF" class="me-1">mdi-plus</v-icon>
          <span>إضافة مركبة</span>
        </button>

        <button type="button" class="op-btn-secondary" @click="openVehicleTypesModal">
          <v-icon size="16" color="#082C69" class="me-1">mdi-format-list-bulleted-type</v-icon>
          <span>إدارة أنواع المركبات</span>
        </button>

        <button type="button" class="op-btn-refresh" @click="fetchVehicles" :disabled="tableLoading">
          <v-icon size="16" color="#082C69" class="me-1">mdi-refresh</v-icon>
          <span>تحديث</span>
        </button>
      </div>
    </div>

    <!-- ==================== 6 KPI CARDS ==================== -->
    <div class="op-kpi-grid mb-6">
      <!-- 1. Total Vehicles -->
      <div class="op-kpi-card">
        <div>
          <div class="kpi-label">إجمالي المركبات</div>
          <div class="kpi-value text-navy">{{ kpis.total_vehicles }}</div>
          <div class="caption text-muted mt-1">أسطول النقل المسجل</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #EEF2FF; color: #082C69;">
          <v-icon size="22" color="#082C69">mdi-bus-multiple</v-icon>
        </div>
      </div>

      <!-- 2. Assigned Vehicles -->
      <div class="op-kpi-card">
        <div>
          <div class="kpi-label">المركبات المخصصة</div>
          <div class="kpi-value text-emerald">{{ kpis.assigned_vehicles }}</div>
          <div class="caption text-muted mt-1">مرتبطة بسائق نشط</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #DCFCE7; color: #16A34A;">
          <v-icon size="22" color="#16A34A">mdi-account-check</v-icon>
        </div>
      </div>

      <!-- 3. Unassigned Vehicles -->
      <div class="op-kpi-card">
        <div>
          <div class="kpi-label">المركبات غير المخصصة</div>
          <div class="kpi-value text-amber">{{ kpis.unassigned_vehicles }}</div>
          <div class="caption text-muted mt-1">شاغرة وبدون سائق</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #FFF3D6; color: #D97706;">
          <v-icon size="22" color="#D97706">mdi-account-off-outline</v-icon>
        </div>
      </div>

      <!-- 4. Expired Licenses -->
      <div class="op-kpi-card">
        <div>
          <div class="kpi-label">الرخص المنتهية</div>
          <div class="kpi-value text-rose">{{ kpis.expired_licenses }}</div>
          <div class="caption text-muted mt-1">تتطلب تجديد فوري</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #FEE2E2; color: #DC2626;">
          <v-icon size="22" color="#DC2626">mdi-alert-circle-outline</v-icon>
        </div>
      </div>

      <!-- 5. Near Expiry Licenses -->
      <div class="op-kpi-card">
        <div>
          <div class="kpi-label">الرخص القريبة من الانتهاء</div>
          <div class="kpi-value text-amber">{{ kpis.near_expiry_licenses }}</div>
          <div class="caption text-muted mt-1">خلال 30 يوماً</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #FFF3D6; color: #D97706;">
          <v-icon size="22" color="#D97706">mdi-clock-alert-outline</v-icon>
        </div>
      </div>

      <!-- 6. Vehicle Types -->
      <div class="op-kpi-card">
        <div>
          <div class="kpi-label">أنواع المركبات</div>
          <div class="kpi-value text-blue">{{ kpis.vehicle_types_count }}</div>
          <div class="caption text-muted mt-1">تصنيفات الأسطول</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #E0F2FE; color: #0284C7;">
          <v-icon size="22" color="#0284C7">mdi-format-list-bulleted-type</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== FILTER BAR ==================== -->
    <div class="op-filter-bar mb-6">
      <div class="filter-controls-group">
        <!-- Search Input -->
        <div class="filter-item-wrapper search-wrapper">
          <label class="filter-label">البحث</label>
          <div class="op-search-input-box">
            <v-icon size="18" color="#63738A" class="me-2">mdi-magnify</v-icon>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="ابحث برقم اللوحة، النوع، السعة أو السائق..."
              class="op-native-input"
              @input="onSearchInput"
              @keydown.enter="onSearchEnter"
            />
            <button v-if="searchQuery" class="clear-search-btn" @click="clearSearch">
              <v-icon size="14" color="#94A3B8">mdi-close-circle</v-icon>
            </button>
          </div>
        </div>

        <!-- Vehicle Type Filter -->
        <div class="filter-item-wrapper">
          <label class="filter-label">نوع المركبة</label>
          <v-select
            v-model="vehicleTypeFilter"
            :items="vehicleTypeOptions"
            item-text="name"
            item-value="id"
            dense
            outlined
            hide-details
            class="op-custom-select"
            @change="onFilterChange"
          ></v-select>
        </div>

        <!-- Assignment Status Filter -->
        <div class="filter-item-wrapper">
          <label class="filter-label">حالة التخصيص</label>
          <v-select
            v-model="assignmentStatusFilter"
            :items="assignmentStatusOptions"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="op-custom-select"
            @change="onFilterChange"
          ></v-select>
        </div>

        <!-- License Status Filter -->
        <div class="filter-item-wrapper">
          <label class="filter-label">حالة الترخيص</label>
          <v-select
            v-model="licenseStatusFilter"
            :items="licenseStatusOptions"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="op-custom-select"
            @change="onFilterChange"
          ></v-select>
        </div>
      </div>

      <div class="filter-actions-group">
        <button type="button" class="op-filter-btn" @click="resetFilters">
          <v-icon size="16" color="#63738A" class="me-1">mdi-restore</v-icon>
          <span>إعادة تعيين</span>
        </button>
      </div>
    </div>

    <!-- ==================== VEHICLES TABLE & PAGINATION ==================== -->
    <div class="op-table-card mb-5">
      <v-data-table
        :headers="headers"
        :items="vehiclesList"
        :loading="tableLoading"
        :server-items-length="totalVehicles"
        :page.sync="page"
        :items-per-page.sync="perPage"
        hide-default-footer
        class="op-data-table"
      >
        <!-- Vehicle ID -->
        <template v-slot:item.id="{ item }">
          <span class="font-weight-bold text-navy" style="font-size: 14px;">
            #{{ String(item.id).padStart(3, '0') }}
          </span>
        </template>

        <!-- Plate Number -->
        <template v-slot:item.license="{ item }">
          <div class="d-flex align-center">
            <span class="op-plate-badge">{{ item.license }}</span>
          </div>
        </template>

        <!-- Vehicle Type -->
        <template v-slot:item.vehicle_type="{ item }">
          <div class="d-flex align-center">
            <v-icon size="17" color="#082C69" class="me-2">mdi-bus</v-icon>
            <span class="font-weight-medium text-navy">{{ item.vehicle_type.display_name }}</span>
          </div>
        </template>

        <!-- Capacity -->
        <template v-slot:item.capacity="{ item }">
          <span class="font-weight-bold text-navy">{{ item.capacity }}</span>
          <span class="caption text-muted ms-1">راكب</span>
        </template>

        <!-- Current Driver -->
        <template v-slot:item.driver="{ item }">
          <div v-if="item.driver" class="d-flex align-center py-2">
            <div
              class="driver-avatar-circle me-3"
              :style="{ backgroundColor: getAvatarColor(item.driver.name).bg, color: getAvatarColor(item.driver.name).text }"
            >
              <img v-if="item.driver.avatar" :src="item.driver.avatar" :alt="item.driver.name" class="driver-avatar-img" />
              <span v-else>{{ getInitials(item.driver.name) }}</span>
            </div>
            <div>
              <div class="driver-name-text">{{ item.driver.name }}</div>
              <div class="caption text-muted" dir="ltr">{{ item.driver.phone }}</div>
            </div>
          </div>
          <div v-else class="vehicle-unlinked-text">
            <v-icon size="15" color="#94A3B8" class="me-1">mdi-account-off</v-icon>
            <span>— سائق غير محدد</span>
          </div>
        </template>

        <!-- License Status Badge -->
        <template v-slot:item.license_status_info="{ item }">
          <div
            class="op-status-badge d-inline-flex"
            :style="getLicenseBadgeStyle(item.license_status_info)"
          >
            <v-icon size="13" class="me-1" :color="getLicenseBadgeStyle(item.license_status_info).color">
              {{ getLicenseIcon(item.license_status_info.key) }}
            </v-icon>
            <span>{{ item.license_status_info.label }}</span>
          </div>
        </template>

        <!-- License Expiry Date -->
        <template v-slot:item.license_expiry_date="{ item }">
          <span v-if="item.license_expiry_date" class="font-weight-medium text-navy">
            {{ item.license_expiry_date }}
          </span>
          <span v-else class="text-muted">غير مسجل</span>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center justify-center gap-1">
            <!-- Details & History -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="op-row-action-btn"
                  v-bind="attrs"
                  v-on="on"
                  @click="openDetailsModal(item)"
                >
                  <v-icon size="16" color="#082C69">mdi-eye-outline</v-icon>
                </button>
              </template>
              <span>تفاصيل المركبة وسجل التخصيص</span>
            </v-tooltip>

            <!-- Edit Vehicle -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="op-row-action-btn"
                  v-bind="attrs"
                  v-on="on"
                  @click="openEditVehicleModal(item)"
                >
                  <v-icon size="16" color="#D97706">mdi-pencil-outline</v-icon>
                </button>
              </template>
              <span>تعديل بيانات المركبة</span>
            </v-tooltip>

            <!-- Assign / Change Driver -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="op-row-action-btn"
                  v-bind="attrs"
                  v-on="on"
                  @click="openAssignDriverModal(item)"
                >
                  <v-icon size="16" :color="item.driver ? '#0284C7' : '#10B981'">
                    {{ item.driver ? 'mdi-account-switch' : 'mdi-account-plus' }}
                  </v-icon>
                </button>
              </template>
              <span>{{ item.driver ? 'تغيير السائق المخصص' : 'تعيين سائق للمركبة' }}</span>
            </v-tooltip>

            <!-- Assignment History Direct Link -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="op-row-action-btn"
                  v-bind="attrs"
                  v-on="on"
                  @click="openHistoryDirect(item)"
                >
                  <v-icon size="16" color="#64748B">mdi-history</v-icon>
                </button>
              </template>
              <span>سجل تخصيص السائقين</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="py-12 text-center">
            <v-icon size="48" color="#94A3B8" class="mb-3">mdi-bus-stop</v-icon>
            <div class="font-weight-bold text-navy mb-1" style="font-size: 16px;">لا توجد مركبات مطابقة</div>
            <div class="caption text-muted">يرجى تعديل مصطلحات البحث أو الفلاتر المختارة</div>
          </div>
        </template>
      </v-data-table>

      <!-- Custom Pagination Bar -->
      <div class="op-pagination-footer">
        <div class="d-flex align-center gap-3 flex-wrap">
          <div class="d-flex align-center gap-2">
            <span class="pagination-label">عرض</span>
            <select v-model="perPage" class="op-rows-select" @change="onPerPageChange(perPage)">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
            </select>
          </div>
          <span class="pagination-info">{{ paginationText }}</span>
        </div>

        <div class="d-flex align-center gap-1">
          <button
            type="button"
            class="op-page-nav-btn"
            :disabled="page <= 1"
            title="الصفحة السابقة"
            @click="onPageChange(page - 1)"
          >
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>

          <template v-for="(p, idx) in visiblePages">
            <span v-if="p === '...'" :key="'ellipsis-' + idx" class="op-page-ellipsis">...</span>
            <button
              v-else
              :key="'page-' + p"
              type="button"
              class="op-page-number-btn"
              :class="{ active: p === page }"
              @click="onPageChange(p)"
            >
              {{ p }}
            </button>
          </template>

          <button
            type="button"
            class="op-page-nav-btn"
            :disabled="page >= totalPages"
            title="الصفحة التالية"
            @click="onPageChange(page + 1)"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== MODAL 1: ASSIGN DRIVER TO VEHICLE ==================== -->
    <v-dialog v-model="assignModalOpen" max-width="860" persistent content-class="op-details-dialog">
      <v-card v-if="selectedVehicleForAssign" class="op-dialog-card d-flex flex-column" style="max-height: 90vh;">
        <!-- Header -->
        <div class="op-dialog-header d-flex align-center justify-space-between px-6 py-4">
          <div class="d-flex align-center">
            <div class="kpi-icon-side me-3" style="background-color: #EEF2FF; color: #082C69; width: 44px; height: 44px;">
              <v-icon size="22" color="#082C69">mdi-account-switch</v-icon>
            </div>
            <div>
              <div class="font-weight-bold text-navy" style="font-size: 18px; line-height: 1.2;">
                تعيين سائق للمركبة
              </div>
              <div class="caption text-muted mt-1">
                اختر من السائقين المتاحين أو قم بإلغاء تخصيص السائق الحالي من هذه المركبة
              </div>
            </div>
          </div>
          <v-btn icon small @click="assignModalOpen = false" class="close-modal-btn">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Scrollable Modal Body -->
        <div class="op-modal-scroll-body px-6 py-4 flex-grow-1 overflow-y-auto">
          <!-- 1. Vehicle Summary Card -->
          <div class="vehicle-summary-banner mb-4">
            <div class="summary-cell">
              <span class="detail-label">معرف المركبة</span>
              <strong class="text-navy">#{{ String(selectedVehicleForAssign.id).padStart(3, '0') }}</strong>
            </div>
            <div class="summary-cell">
              <span class="detail-label">لوحة المركبة</span>
              <span class="op-plate-badge">{{ selectedVehicleForAssign.license }}</span>
            </div>
            <div class="summary-cell">
              <span class="detail-label">نوع المركبة</span>
              <strong class="text-navy">{{ selectedVehicleForAssign.vehicle_type.display_name }}</strong>
            </div>
            <div class="summary-cell">
              <span class="detail-label">السعة</span>
              <strong class="text-navy">{{ selectedVehicleForAssign.capacity }} راكب</strong>
            </div>
            <div class="summary-cell">
              <span class="detail-label">حالة التخصيص</span>
              <span
                class="op-status-badge d-inline-block"
                :style="{
                  backgroundColor: selectedVehicleForAssign.driver ? '#DCFCE7' : '#FFF3D6',
                  color: selectedVehicleForAssign.driver ? '#15803D' : '#C56A00'
                }"
              >
                {{ selectedVehicleForAssign.driver ? 'مخصصة' : 'غير مخصصة' }}
              </span>
            </div>
          </div>

          <!-- 2. Current Driver Section -->
          <div class="op-info-card mb-4">
            <div class="op-card-header justify-space-between">
              <div class="d-flex align-center">
                <v-icon size="20" color="#082C69" class="me-2">mdi-account-tie</v-icon>
                <span class="op-card-title">السائق الحالي للمركبة</span>
              </div>
              <span v-if="selectedVehicleForAssign.driver" class="caption text-muted">تخصيص جاري</span>
            </div>

            <div v-if="selectedVehicleForAssign.driver" class="d-flex align-center justify-space-between flex-wrap gap-3">
              <div class="d-flex align-center">
                <div
                  class="profile-hero-avatar me-3"
                  :style="{ backgroundColor: getAvatarColor(selectedVehicleForAssign.driver.name).bg, color: getAvatarColor(selectedVehicleForAssign.driver.name).text, width: '48px', height: '48px', fontSize: '16px' }"
                >
                  <img v-if="selectedVehicleForAssign.driver.avatar" :src="selectedVehicleForAssign.driver.avatar" :alt="selectedVehicleForAssign.driver.name" />
                  <span v-else>{{ getInitials(selectedVehicleForAssign.driver.name) }}</span>
                </div>
                <div>
                  <div class="font-weight-bold text-navy" style="font-size: 15px;">{{ selectedVehicleForAssign.driver.name }}</div>
                  <div class="caption text-muted" dir="ltr">{{ selectedVehicleForAssign.driver.phone }}</div>
                </div>
              </div>

              <!-- Unassign Action Button -->
              <v-btn
                depressed
                color="#FEE2E2"
                class="text-rose font-weight-bold"
                :loading="isSubmittingAction"
                @click="submitUnassignDriver"
              >
                <v-icon size="16" color="#DC2626" class="me-1">mdi-account-off</v-icon>
                <span>إلغاء التخصيص</span>
              </v-btn>
            </div>

            <!-- Empty Driver State -->
            <div v-else class="text-center py-4 text-muted">
              <v-icon size="36" color="#94A3B8" class="mb-1">mdi-account-off-outline</v-icon>
              <div class="font-weight-bold text-navy">لا يوجد سائق مخصص لهذه المركبة حالياً</div>
              <div class="caption">يمكنك اختيار وتعيين سائق متاح من القائمة أدناه</div>
            </div>
          </div>

          <!-- 3. Available Drivers Section -->
          <div class="op-info-card">
            <div class="op-card-header justify-space-between">
              <div class="d-flex align-center">
                <v-icon size="20" color="#082C69" class="me-2">mdi-account-group-outline</v-icon>
                <span class="op-card-title">السائقون المتاحون للتعيين</span>
              </div>
              <span class="caption text-muted">إجمالي المتاح: {{ filteredAvailableDrivers.length }}</span>
            </div>

            <!-- Search & Filter Inside Modal -->
            <div class="d-flex align-center gap-2 mb-3 flex-wrap">
              <div class="op-search-input-box flex-grow-1" style="max-width: 380px;">
                <v-icon size="16" color="#63738A" class="me-2">mdi-magnify</v-icon>
                <input
                  type="text"
                  v-model="driverSearchQuery"
                  placeholder="ابحث باسم السائق أو رقم الهاتف..."
                  class="op-native-input"
                />
              </div>

              <div class="d-flex align-center gap-1">
                <button
                  type="button"
                  class="op-filter-chip-btn"
                  :class="{ active: driverFilterTab === 'all' }"
                  @click="driverFilterTab = 'all'"
                >
                  الكل
                </button>
                <button
                  type="button"
                  class="op-filter-chip-btn"
                  :class="{ active: driverFilterTab === 'available' }"
                  @click="driverFilterTab = 'available'"
                >
                  المتاحون فقط
                </button>
                <button
                  type="button"
                  class="op-filter-chip-btn"
                  :class="{ active: driverFilterTab === 'conflicts' }"
                  @click="driverFilterTab = 'conflicts'"
                >
                  مع تعارض
                </button>
              </div>
            </div>

            <!-- Drivers List -->
            <div v-if="loadingAvailableDrivers" class="py-8 text-center text-muted">
              <v-progress-circular indeterminate color="#082C69" size="28" class="mb-2"></v-progress-circular>
              <div>جاري تحميل قائمة السائقين...</div>
            </div>

            <div v-else-if="filteredAvailableDrivers.length === 0" class="py-6 text-center text-muted">
              <v-icon size="32" color="#94A3B8" class="mb-1">mdi-account-search-outline</v-icon>
              <div>لا يوجد سائقين مطابقين لخيارات البحث</div>
            </div>

            <div v-else class="available-drivers-stack">
              <div
                v-for="d in filteredAvailableDrivers"
                :key="d.id"
                class="driver-selection-row"
                :class="{ 'row-conflict': !d.is_available, 'row-current': d.is_assigned_to_this_bus }"
              >
                <!-- Driver Info -->
                <div class="d-flex align-center">
                  <div
                    class="driver-avatar-circle me-3"
                    :style="{ backgroundColor: getAvatarColor(d.name).bg, color: getAvatarColor(d.name).text, width: '40px', height: '40px', fontSize: '14px' }"
                  >
                    <img v-if="d.avatar" :src="d.avatar" :alt="d.name" />
                    <span v-else>{{ getInitials(d.name) }}</span>
                  </div>
                  <div>
                    <div class="font-weight-bold text-navy" style="font-size: 14px;">
                      {{ d.name }}
                      <span v-if="d.is_assigned_to_this_bus" class="badge-current-assigned ms-2">السائق الحالي</span>
                    </div>
                    <div class="caption text-muted" dir="ltr">{{ d.phone }}</div>
                  </div>
                </div>

                <!-- Availability Badge / Conflict Note -->
                <div class="d-flex align-center gap-3">
                  <div v-if="d.is_available" class="op-status-badge" style="background-color: #DCFCE7; color: #15803D;">
                    <v-icon size="13" color="#15803D" class="me-1">mdi-check-circle</v-icon>
                    <span>متاح الآن</span>
                  </div>
                  <div v-else class="op-status-badge" style="background-color: #FFF3D6; color: #C56A00;">
                    <v-icon size="13" color="#C56A00" class="me-1">mdi-alert-circle</v-icon>
                    <span>{{ d.conflict_reason || 'غير متاح حالياً' }}</span>
                  </div>

                  <!-- Assign Action Button -->
                  <v-btn
                    v-if="!d.is_assigned_to_this_bus"
                    depressed
                    small
                    color="#082C69"
                    class="white--text font-weight-bold"
                    :loading="isSubmittingAction && targetAssignDriverId === d.id"
                    :disabled="!d.is_available || isSubmittingAction"
                    @click="submitAssignDriver(d.id)"
                  >
                    <v-icon size="14" class="me-1">mdi-check</v-icon>
                    <span>تعيين</span>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="op-dialog-footer px-6 py-3 d-flex align-center justify-end">
          <v-btn text color="#64748B" @click="assignModalOpen = false" :disabled="isSubmittingAction">
            إغلاق
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ==================== MODAL 2: VEHICLE DETAILS & ASSIGNMENT HISTORY ==================== -->
    <v-dialog v-model="detailsModalOpen" max-width="1080" persistent content-class="op-details-dialog">
      <v-card v-if="selectedVehicleDetails" class="op-dialog-card d-flex flex-column" style="max-height: 90vh;">
        <!-- Header -->
        <div class="op-dialog-header d-flex align-center justify-space-between px-6 py-4">
          <div class="d-flex align-center">
            <div class="kpi-icon-side me-3" style="background-color: #EEF2FF; color: #082C69; width: 46px; height: 46px;">
              <v-icon size="24" color="#082C69">mdi-bus-side</v-icon>
            </div>
            <div>
              <div class="font-weight-bold text-navy" style="font-size: 19px; line-height: 1.2;">
                تفاصيل المركبة وسجل التخصيص — {{ selectedVehicleDetails.bus.license }}
              </div>
              <div class="caption text-muted mt-1">
                عرض جميع مواصفات المركبة الحالية وسجل تخصيص السائقين والنشاط المسند
              </div>
            </div>
          </div>
          <v-btn icon small @click="detailsModalOpen = false" class="close-modal-btn">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Scrollable Modal Body -->
        <div class="op-modal-scroll-body px-6 py-4 flex-grow-1 overflow-y-auto">
          <!-- 1. Top Vehicle KPIs -->
          <div class="op-sub-kpi-grid mb-4">
            <div class="op-sub-kpi-card">
              <div>
                <div class="kpi-label">معرف المركبة</div>
                <div class="font-weight-bold text-navy" style="font-size: 16px;">
                  #{{ String(selectedVehicleDetails.bus.id).padStart(3, '0') }}
                </div>
                <div class="caption text-muted mt-1">رقم النظام</div>
              </div>
              <div class="kpi-icon-side" style="background-color: #EEF2FF; color: #082C69;">
                <v-icon size="18" color="#082C69">mdi-identifier</v-icon>
              </div>
            </div>

            <div class="op-sub-kpi-card">
              <div>
                <div class="kpi-label">لوحة المركبة</div>
                <div class="font-weight-bold text-navy" style="font-size: 16px;">
                  {{ selectedVehicleDetails.bus.license }}
                </div>
                <div class="caption text-muted mt-1">جمهورية مصر العربية</div>
              </div>
              <div class="kpi-icon-side" style="background-color: #EEF2FF; color: #082C69;">
                <v-icon size="18" color="#082C69">mdi-card-text-outline</v-icon>
              </div>
            </div>

            <div class="op-sub-kpi-card">
              <div>
                <div class="kpi-label">نوع المركبة</div>
                <div class="font-weight-bold text-navy" style="font-size: 15px;">
                  {{ selectedVehicleDetails.bus.vehicle_type.display_name }}
                </div>
                <div class="caption text-muted mt-1">سعة {{ selectedVehicleDetails.bus.capacity }} راكب</div>
              </div>
              <div class="kpi-icon-side" style="background-color: #E0F2FE; color: #0284C7;">
                <v-icon size="18" color="#0284C7">mdi-bus</v-icon>
              </div>
            </div>

            <div class="op-sub-kpi-card">
              <div>
                <div class="kpi-label">حالة التخصيص</div>
                <div
                  class="font-weight-bold"
                  :class="selectedVehicleDetails.bus.driver ? 'text-emerald' : 'text-amber'"
                  style="font-size: 15px;"
                >
                  {{ selectedVehicleDetails.bus.driver ? 'مخصصة' : 'غير مخصصة' }}
                </div>
                <div class="caption text-muted mt-1">
                  {{ selectedVehicleDetails.bus.driver ? 'مرتبطة بسائق' : 'شاغرة حالياً' }}
                </div>
              </div>
              <div
                class="kpi-icon-side"
                :style="{
                  backgroundColor: selectedVehicleDetails.bus.driver ? '#DCFCE7' : '#FFF3D6',
                  color: selectedVehicleDetails.bus.driver ? '#16A34A' : '#D97706'
                }"
              >
                <v-icon size="18" :color="selectedVehicleDetails.bus.driver ? '#16A34A' : '#D97706'">
                  {{ selectedVehicleDetails.bus.driver ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                </v-icon>
              </div>
            </div>

            <div class="op-sub-kpi-card">
              <div>
                <div class="kpi-label">حالة الرخصة</div>
                <div
                  class="font-weight-bold"
                  :style="{ color: getLicenseBadgeStyle(selectedVehicleDetails.bus.license_status_info).color }"
                  style="font-size: 14.5px;"
                >
                  {{ selectedVehicleDetails.bus.license_status_info.label }}
                </div>
                <div class="caption text-muted mt-1">
                  {{ selectedVehicleDetails.bus.license_expiry_date || 'غير مسجل' }}
                </div>
              </div>
              <div
                class="kpi-icon-side"
                :style="{
                  backgroundColor: getLicenseBadgeStyle(selectedVehicleDetails.bus.license_status_info).backgroundColor
                }"
              >
                <v-icon size="18" :color="getLicenseBadgeStyle(selectedVehicleDetails.bus.license_status_info).color">
                  {{ getLicenseIcon(selectedVehicleDetails.bus.license_status_info.key) }}
                </v-icon>
              </div>
            </div>
          </div>

          <!-- 2. Current Assigned Driver Card -->
          <div class="op-info-card mb-4">
            <div class="op-card-header justify-space-between">
              <div class="d-flex align-center">
                <v-icon size="20" color="#082C69" class="me-2">mdi-account-tie</v-icon>
                <span class="op-card-title">السائق المخصص حالياً</span>
              </div>
              <div v-if="selectedVehicleDetails.current_captain" class="d-flex align-center gap-2">
                <span class="op-status-badge" style="background-color: #DCFCE7; color: #15803D;">
                  تخصيص جاري
                </span>
                <v-btn
                  x-small
                  depressed
                  color="#082C69"
                  class="white--text font-weight-bold ms-2"
                  @click="openAssignFromDetails"
                >
                  تغيير السائق
                </v-btn>
              </div>
            </div>

            <div v-if="selectedVehicleDetails.current_captain" class="d-flex align-center justify-space-between flex-wrap gap-4 py-2">
              <div class="d-flex align-center">
                <div
                  class="profile-hero-avatar me-3"
                  :style="{ backgroundColor: getAvatarColor(selectedVehicleDetails.current_captain.driver_name).bg, color: getAvatarColor(selectedVehicleDetails.current_captain.driver_name).text, width: '52px', height: '52px', fontSize: '18px' }"
                >
                  <img v-if="selectedVehicleDetails.current_captain.driver_avatar" :src="selectedVehicleDetails.current_captain.driver_avatar" :alt="selectedVehicleDetails.current_captain.driver_name" />
                  <span v-else>{{ getInitials(selectedVehicleDetails.current_captain.driver_name) }}</span>
                </div>
                <div>
                  <div class="font-weight-bold text-navy" style="font-size: 16px;">
                    {{ selectedVehicleDetails.current_captain.driver_name }}
                  </div>
                  <div class="caption text-muted mt-1" dir="ltr">
                    <v-icon size="14" color="#63738A" class="me-1">mdi-phone-outline</v-icon>
                    <span>{{ selectedVehicleDetails.current_captain.driver_phone }}</span>
                  </div>
                </div>
              </div>

              <div class="d-flex align-center gap-4 flex-wrap">
                <div class="meta-spec-item">
                  <v-icon size="16" color="#082C69" class="me-2">mdi-calendar-start</v-icon>
                  <span class="spec-label">بداية التخصيص:</span>
                  <strong class="spec-val text-navy">{{ selectedVehicleDetails.current_captain.started_at }}</strong>
                </div>
                <div class="meta-spec-item">
                  <v-icon size="16" color="#082C69" class="me-2">mdi-clock-outline</v-icon>
                  <span class="spec-label">مدة الاستخدام:</span>
                  <strong class="spec-val text-navy">{{ selectedVehicleDetails.current_captain.duration_human }}</strong>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-6 text-muted">
              <v-icon size="40" color="#94A3B8" class="mb-2">mdi-account-off-outline</v-icon>
              <div class="font-weight-bold text-navy">لا يوجد سائق مخصص حالياً لهذه المركبة</div>
              <div class="caption text-muted mt-1 mb-3">يمكنك تعيين سائق متاح من الأسطول لتشغيل هذه المركبة</div>
              <v-btn
                depressed
                small
                color="#082C69"
                class="white--text font-weight-bold"
                @click="openAssignFromDetails"
              >
                <v-icon size="15" class="me-1">mdi-account-plus</v-icon>
                <span>تعيين سائق الآن</span>
              </v-btn>
            </div>
          </div>

          <!-- 3. Driver Assignment History Table -->
          <div class="op-info-card">
            <div class="op-card-header justify-space-between flex-wrap gap-2">
              <div class="d-flex align-center">
                <v-icon size="20" color="#082C69" class="me-2">mdi-history</v-icon>
                <span class="op-card-title">سجل تخصيص السائقين</span>
              </div>
              <div class="d-flex align-center gap-2">
                <span class="caption text-muted">إجمالي السجلات: {{ selectedVehicleDetails.assignment_history ? selectedVehicleDetails.assignment_history.length : 0 }}</span>
                <v-btn
                  small
                  outlined
                  color="#082C69"
                  class="font-weight-bold"
                  :disabled="!selectedVehicleDetails.assignment_history || selectedVehicleDetails.assignment_history.length === 0"
                  @click="exportCsv(selectedVehicleDetails.bus.id)"
                >
                  <v-icon size="15" class="me-1">mdi-download</v-icon>
                  <span>تصدير CSV</span>
                </v-btn>
              </div>
            </div>

            <div class="op-modal-table">
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-right">#</th>
                      <th class="text-right">السائق</th>
                      <th class="text-right">رقم الهاتف</th>
                      <th class="text-right">تاريخ بداية التخصيص</th>
                      <th class="text-right">تاريخ نهاية التخصيص</th>
                      <th class="text-right">مدة الاستخدام</th>
                      <th class="text-center">حالة التخصيص</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(h, idx) in selectedVehicleDetails.assignment_history" :key="idx">
                      <td class="font-weight-bold text-navy">{{ idx + 1 }}</td>
                      <td class="font-weight-bold text-navy">{{ h.driver_name }}</td>
                      <td dir="ltr" class="text-right">{{ h.driver_phone }}</td>
                      <td>{{ h.started_at }}</td>
                      <td>{{ h.ended_at || 'مستمر حتى الآن' }}</td>
                      <td class="font-weight-medium text-navy">{{ h.duration_human }}</td>
                      <td class="text-center">
                        <span
                          class="op-status-badge d-inline-block"
                          :style="{
                            backgroundColor: h.is_current ? '#DCFCE7' : '#EEF1F5',
                            color: h.is_current ? '#15803D' : '#596579',
                            borderColor: h.is_current ? '#BBF7D0' : '#CBD5E1'
                          }"
                        >
                          {{ h.status_label }}
                        </span>
                      </td>
                    </tr>
                    <tr v-if="!selectedVehicleDetails.assignment_history || selectedVehicleDetails.assignment_history.length === 0">
                      <td colspan="7" class="text-center py-6 text-muted">
                        لا يوجد سجل تخصيص سابق لهذه المركبة.
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="op-dialog-footer px-6 py-3 d-flex align-center justify-end">
          <v-btn depressed color="#64748B" class="white--text font-weight-bold" @click="detailsModalOpen = false">
            إغلاق
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ==================== MODAL 3: VEHICLE TYPES MODAL ==================== -->
    <v-dialog v-model="vehicleTypesModalOpen" max-width="920" persistent content-class="op-details-dialog">
      <v-card class="op-dialog-card d-flex flex-column" style="max-height: 90vh;">
        <!-- Header -->
        <div class="op-dialog-header d-flex align-center justify-space-between px-6 py-4">
          <div class="d-flex align-center">
            <div class="kpi-icon-side me-3" style="background-color: #EEF2FF; color: #082C69; width: 44px; height: 44px;">
              <v-icon size="22" color="#082C69">mdi-format-list-bulleted-type</v-icon>
            </div>
            <div>
              <div class="font-weight-bold text-navy" style="font-size: 18px; line-height: 1.2;">
                أنواع وسائل النقل
              </div>
              <div class="caption text-muted mt-1">
                إدارة أنواع وسائل النقل المستخدمة في النظام
              </div>
            </div>
          </div>
          <v-btn icon small @click="closeVehicleTypesModal" class="close-modal-btn">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Scrollable Modal Body -->
        <div class="op-modal-scroll-body px-6 py-4 flex-grow-1 overflow-y-auto">
          <!-- Top Form: Add / Edit Type -->
          <div class="op-info-card mb-5">
            <div class="op-card-header justify-space-between">
              <div class="d-flex align-center">
                <v-icon size="20" color="#082C69" class="me-2">
                  {{ isEditingVehicleType ? 'mdi-pencil-box-outline' : 'mdi-plus-box-outline' }}
                </v-icon>
                <span class="op-card-title">
                  {{ isEditingVehicleType ? 'تعديل نوع وسيلة نقل' : 'إضافة نوع وسيلة نقل' }}
                </span>
              </div>
              <div v-if="isEditingVehicleType" class="d-flex align-center gap-2">
                <span class="op-status-badge" style="background-color: #FEF3C7; color: #92400E;">
                  جاري التعديل: #{{ vehicleTypeFormData.id }}
                </span>
                <button type="button" class="caption text-muted font-weight-bold" @click="resetVehicleTypeForm" style="text-decoration: underline; background: none; border: none; cursor: pointer;">
                  إلغاء التعديل
                </button>
              </div>
            </div>

            <div class="px-4 py-3">
              <v-alert v-if="vehicleTypeFormError" type="error" dense outlined class="mb-3">
                {{ vehicleTypeFormError }}
              </v-alert>

              <div class="row">
                <div class="col-12 col-md-4 py-1">
                  <label class="form-label-bold">الاسم بالعربية <span class="text-rose">*</span></label>
                  <input
                    type="text"
                    v-model="vehicleTypeFormData.name_ar"
                    placeholder="مثال: تويوتا كوستر"
                    class="op-form-input"
                  />
                </div>
                <div class="col-12 col-md-4 py-1">
                  <label class="form-label-bold">الاسم بالإنجليزية <span class="text-rose">*</span></label>
                  <input
                    type="text"
                    v-model="vehicleTypeFormData.name_en"
                    placeholder="Toyota Coaster"
                    class="op-form-input"
                    dir="ltr"
                  />
                </div>
                <div class="col-12 col-md-4 py-1">
                  <label class="form-label-bold">عدد المقاعد <span class="text-rose">*</span></label>
                  <input
                    type="number"
                    min="1"
                    v-model.number="vehicleTypeFormData.seats_count"
                    placeholder="28"
                    class="op-form-input"
                  />
                </div>
              </div>

              <div class="d-flex align-center justify-end gap-2 mt-3">
                <button
                  type="button"
                  class="op-btn-form-cancel"
                  @click="resetVehicleTypeForm"
                  v-if="isEditingVehicleType"
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  class="op-btn-primary"
                  @click="saveVehicleType"
                  :disabled="vehicleTypeFormLoading"
                >
                  <v-icon size="16" color="#FFFFFF" class="me-1">mdi-content-save-check</v-icon>
                  <span>{{ isEditingVehicleType ? 'حفظ التغييرات' : 'إضافة النوع' }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Bottom List Table -->
          <div class="op-info-card">
            <div class="op-card-header justify-space-between flex-wrap gap-2">
              <div class="d-flex align-center">
                <v-icon size="20" color="#082C69" class="me-2">mdi-format-list-numbered</v-icon>
                <span class="op-card-title">قائمة أنواع وسائل النقل</span>
              </div>
              <div class="d-flex align-center gap-2">
                <div class="op-search-input-box" style="width: 240px; height: 36px;">
                  <v-icon size="16" color="#63738A" class="me-2">mdi-magnify</v-icon>
                  <input
                    type="text"
                    v-model="vehicleTypeSearch"
                    placeholder="بحث في الأنواع..."
                    class="op-native-input"
                  />
                </div>
              </div>
            </div>

            <div class="op-modal-table">
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-right">#</th>
                      <th class="text-right">الاسم بالعربية</th>
                      <th class="text-right">الاسم بالإنجليزية</th>
                      <th class="text-center">عدد المقاعد</th>
                      <th class="text-center">عدد المركبات المرتبطة</th>
                      <th class="text-center">الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in filteredVehicleTypes" :key="t.id">
                      <td class="font-weight-bold text-navy">#{{ t.id }}</td>
                      <td class="font-weight-bold text-navy">{{ t.name_ar }}</td>
                      <td class="font-weight-medium text-navy" dir="ltr" style="text-align: right;">{{ t.name_en }}</td>
                      <td class="text-center font-weight-bold text-navy">
                        <span class="op-seat-badge">{{ t.seats_count }} مقعد</span>
                      </td>
                      <td class="text-center">
                        <span
                          class="op-status-badge"
                          :style="{
                            backgroundColor: t.buses_count > 0 ? '#EEF2FF' : '#F1F5F9',
                            color: t.buses_count > 0 ? '#082C69' : '#64748B'
                          }"
                        >
                          {{ t.buses_count }} مركبة
                        </span>
                      </td>
                      <td class="text-center">
                        <div class="d-flex align-center justify-center gap-1">
                          <button
                            type="button"
                            class="op-row-action-btn"
                            title="تعديل"
                            @click="editVehicleType(t)"
                          >
                            <v-icon size="15" color="#082C69">mdi-pencil</v-icon>
                          </button>
                          <button
                            type="button"
                            class="op-row-action-btn"
                            title="حذف"
                            @click="confirmDeleteVehicleType(t)"
                          >
                            <v-icon size="15" color="#DC2626">mdi-trash-can-outline</v-icon>
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="filteredVehicleTypes.length === 0">
                      <td colspan="6" class="text-center py-6 text-muted">
                        لا توجد أنواع وسائل نقل مطابقة.
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="op-dialog-footer px-6 py-3 d-flex align-center justify-end">
          <v-btn depressed color="#64748B" class="white--text font-weight-bold" @click="closeVehicleTypesModal">
            إغلاق
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Vehicle Type Dialog -->
    <v-dialog v-model="vehicleTypeDeleteDialog" max-width="450" persistent>
      <v-card class="pa-4 text-center">
        <v-icon size="48" color="#DC2626" class="mb-3">mdi-alert-circle-outline</v-icon>
        <div class="font-weight-bold text-navy" style="font-size: 17px;">تأكيد حذف نوع وسيلة النقل</div>
        <div class="caption text-muted mt-2 mb-4">
          هل أنت متأكد من رغبتك في حذف نوع المركبة "{{ vehicleTypeToDelete ? vehicleTypeToDelete.name_ar : '' }}"؟
        </div>
        <div class="d-flex align-center justify-center gap-2">
          <button
            type="button"
            class="op-btn-form-cancel"
            @click="vehicleTypeDeleteDialog = false"
          >
            إلغاء
          </button>
          <v-btn
            depressed
            color="#DC2626"
            class="white--text font-weight-bold"
            :loading="vehicleTypeDeleteLoading"
            @click="deleteVehicleTypeConfirmed"
          >
            نعم، احذف
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ==================== MODAL 4: ADD / EDIT VEHICLE MODAL ==================== -->
    <v-dialog v-model="vehicleFormModalOpen" max-width="600" persistent content-class="op-details-dialog">
      <v-card class="op-dialog-card d-flex flex-column">
        <!-- Header -->
        <div class="op-dialog-header d-flex align-center justify-space-between px-6 py-4">
          <div class="d-flex align-center">
            <div class="kpi-icon-side me-3" style="background-color: #EEF2FF; color: #082C69; width: 44px; height: 44px;">
              <v-icon size="22" color="#082C69">{{ isEditingVehicle ? 'mdi-bus-clock' : 'mdi-bus-multiple' }}</v-icon>
            </div>
            <div>
              <div class="font-weight-bold text-navy" style="font-size: 18px; line-height: 1.2;">
                {{ isEditingVehicle ? 'تعديل بيانات وسيلة النقل' : 'إضافة وسيلة نقل جديدة' }}
              </div>
              <div class="caption text-muted mt-1">
                بيانات وسيلة النقل لتحديث الأسطول في النظام
              </div>
            </div>
          </div>
          <v-btn icon small @click="vehicleFormModalOpen = false" class="close-modal-btn">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Body -->
        <div class="px-6 py-5">
          <v-alert v-if="vehicleFormError" type="error" dense outlined class="mb-4">
            {{ vehicleFormError }}
          </v-alert>

          <!-- 1. Vehicle Type -->
          <div class="form-group mb-4">
            <label class="form-label-bold mb-1 d-block">
              نوع وسيلة النقل <span class="text-rose">*</span>
            </label>
            <v-select
              v-model="vehicleFormData.vehicle_type_id"
              :items="vehicleTypesListFull"
              item-text="name_ar"
              item-value="id"
              dense
              outlined
              hide-details
              placeholder="اختر نوع وسيلة النقل"
              class="op-custom-select"
              @change="onVehicleTypeSelected"
            ></v-select>
          </div>

          <!-- 2. Plate Number -->
          <div class="form-group mb-4">
            <label class="form-label-bold mb-1 d-block">
              لوحة المركبة <span class="text-rose">*</span>
            </label>
            <input
              type="text"
              v-model="vehicleFormData.license"
              placeholder="مثال: أ ب ج 1234"
              class="op-form-input"
            />
          </div>

          <!-- 3. License Expiry Date -->
          <div class="form-group mb-4">
            <label class="form-label-bold mb-1 d-block">
              تاريخ انتهاء الرخصة <span class="text-rose">*</span>
            </label>
            <input
              type="date"
              v-model="vehicleFormData.license_expiry_date"
              class="op-form-input"
            />
          </div>

          <!-- 4. Capacity (Read-only, derived automatically from vehicle type) -->
          <div class="form-group mb-2">
            <label class="form-label-bold mb-1 d-block">
              السعة <span class="text-rose">*</span>
            </label>
            <input
              type="text"
              :value="vehicleFormData.capacity ? vehicleFormData.capacity + ' راكب' : ''"
              readonly
              disabled
              placeholder="تحدد تلقائياً حسب نوع وسيلة النقل"
              class="op-form-input op-input-readonly"
            />
            <div class="caption text-muted mt-1">
              <v-icon size="13" color="#63738A" class="me-1">mdi-information-outline</v-icon>
              <span>تُحدد تلقائياً حسب نوع وسيلة النقل</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="op-dialog-footer px-6 py-4 d-flex align-center justify-end gap-2">
          <button
            type="button"
            class="op-btn-form-cancel"
            @click="vehicleFormModalOpen = false"
          >
            إلغاء
          </button>
          <button
            type="button"
            class="op-btn-primary"
            @click="saveVehicle"
            :disabled="vehicleFormLoading"
          >
            <v-icon size="16" color="#FFFFFF" class="me-1">mdi-content-save-check</v-icon>
            <span>حفظ التغييرات</span>
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import VueElementLoading from 'vue-element-loading';

export default {
  name: 'OperationsVehicles',
  components: {
    VueElementLoading,
  },
  data() {
    return {
      isLoading: false,
      tableLoading: false,
      searchQuery: '',
      searchDebounceTimer: null,
      vehicleTypeFilter: 'all',
      assignmentStatusFilter: 'all',
      licenseStatusFilter: 'all',
      page: 1,
      perPage: 10,
      totalVehicles: 0,
      vehiclesList: [],
      vehicleTypeOptions: [{ id: 'all', name: 'الكل' }],
      kpis: {
        total_vehicles: 0,
        assigned_vehicles: 0,
        unassigned_vehicles: 0,
        expired_licenses: 0,
        near_expiry_licenses: 0,
        vehicle_types_count: 0,
      },
      headers: [
        { text: 'معرف المركبة', value: 'id', sortable: false, width: '12%' },
        { text: 'لوحة المركبة', value: 'license', sortable: false, width: '14%' },
        { text: 'نوع المركبة', value: 'vehicle_type', sortable: false, width: '14%' },
        { text: 'السعة', value: 'capacity', sortable: false, width: '10%' },
        { text: 'السائق الحالي', value: 'driver', sortable: false, width: '22%' },
        { text: 'حالة الترخيص', value: 'license_status_info', sortable: false, width: '13%' },
        { text: 'انتهاء الرخصة', value: 'license_expiry_date', sortable: false, width: '15%' },
        { text: 'الإجراءات', value: 'actions', sortable: false, align: 'center', width: '10%' },
      ],
      assignmentStatusOptions: [
        { text: 'الكل', value: 'all' },
        { text: 'مخصصة', value: 'assigned' },
        { text: 'غير مخصصة', value: 'unassigned' },
      ],
      licenseStatusOptions: [
        { text: 'الكل', value: 'all' },
        { text: 'سارية', value: 'valid' },
        { text: 'تنتهي قريباً', value: 'near_expiry' },
        { text: 'منتهية', value: 'expired' },
        { text: 'غير مسجل', value: 'unrecorded' },
      ],

      // Modal 1: Assign Driver Modal
      assignModalOpen: false,
      selectedVehicleForAssign: null,
      availableDrivers: [],
      loadingAvailableDrivers: false,
      driverSearchQuery: '',
      driverFilterTab: 'all',
      isSubmittingAction: false,
      targetAssignDriverId: null,

      // Modal 2: Details Modal
      detailsModalOpen: false,
      selectedVehicleDetails: null,

      // Modal 3: Vehicle Types Modal
      vehicleTypesModalOpen: false,
      vehicleTypesListFull: [],
      vehicleTypesLoading: false,
      vehicleTypeSearch: '',
      isEditingVehicleType: false,
      vehicleTypeFormData: {
        id: null,
        name_ar: '',
        name_en: '',
        seats_count: null,
      },
      vehicleTypeFormLoading: false,
      vehicleTypeFormError: '',
      vehicleTypeDeleteDialog: false,
      vehicleTypeToDelete: null,
      vehicleTypeDeleteLoading: false,

      // Modal 4: Add / Edit Vehicle Modal
      vehicleFormModalOpen: false,
      isEditingVehicle: false,
      vehicleFormData: {
        id: null,
        vehicle_type_id: null,
        license: '',
        license_expiry_date: '',
        capacity: '',
      },
      vehicleFormLoading: false,
      vehicleFormError: '',
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalVehicles / this.perPage) || 1;
    },
    paginationText() {
      if (this.totalVehicles === 0) return '0 من 0 مركبة';
      const start = (this.page - 1) * this.perPage + 1;
      const end = Math.min(this.page * this.perPage, this.totalVehicles);
      return `${start}-${end} من ${this.totalVehicles} مركبة`;
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.page;
      if (total <= 7) {
        const pages = [];
        for (let i = 1; i <= total; i++) pages.push(i);
        return pages;
      }
      if (current <= 4) {
        return [1, 2, 3, 4, 5, '...', total];
      }
      if (current >= total - 3) {
        return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
      }
      return [1, '...', current - 1, current, current + 1, '...', total];
    },
    filteredAvailableDrivers() {
      let list = this.availableDrivers || [];
      if (this.driverSearchQuery && this.driverSearchQuery.trim() !== '') {
        const q = this.driverSearchQuery.trim().toLowerCase();
        list = list.filter((d) => d.name.toLowerCase().includes(q) || (d.phone && d.phone.includes(q)));
      }
      if (this.driverFilterTab === 'available') {
        list = list.filter((d) => d.is_available);
      } else if (this.driverFilterTab === 'conflicts') {
        list = list.filter((d) => !d.is_available);
      }
      return list;
    },
    filteredVehicleTypes() {
      let list = this.vehicleTypesListFull || [];
      if (this.vehicleTypeSearch && this.vehicleTypeSearch.trim() !== '') {
        const q = this.vehicleTypeSearch.trim().toLowerCase();
        list = list.filter(
          (t) =>
            (t.name_ar && t.name_ar.toLowerCase().includes(q)) ||
            (t.name_en && t.name_en.toLowerCase().includes(q)) ||
            String(t.id).includes(q)
        );
      }
      return list;
    },
  },
  mounted() {
    this.fetchVehicles();
    this.fetchVehicleTypesFull();
  },
  methods: {
    async fetchVehicles() {
      this.tableLoading = true;
      try {
        const params = {
          page: this.page,
          per_page: this.perPage,
          vehicle_type_id: this.vehicleTypeFilter,
          assignment_status: this.assignmentStatusFilter,
          license_status: this.licenseStatusFilter,
        };
        if (this.searchQuery && this.searchQuery.trim() !== '') {
          params.search = this.searchQuery.trim();
        }

        const res = await axios.get('/operations/vehicles', { params });
        if (res.data) {
          const data = res.data;
          this.vehiclesList = (data.vehicles && data.vehicles.data) || [];
          this.totalVehicles = (data.vehicles && data.vehicles.total) || 0;
          if (data.kpis) {
            this.kpis = data.kpis;
          }
          if (data.vehicle_types) {
            this.vehicleTypeOptions = [{ id: 'all', name: 'الكل' }, ...data.vehicle_types];
          }
        }
      } catch (err) {
        console.error('Failed to load vehicles:', err);
        this.$notify({
          title: 'خطأ',
          text: 'تعذر تحميل بيانات وسائل النقل.',
          type: 'error',
        });
      } finally {
        this.tableLoading = false;
        this.isLoading = false;
      }
    },
    onSearchInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.page = 1;
        this.fetchVehicles();
      }, 400);
    },
    onSearchEnter() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.page = 1;
      this.fetchVehicles();
    },
    clearSearch() {
      this.searchQuery = '';
      this.page = 1;
      this.fetchVehicles();
    },
    onFilterChange() {
      this.page = 1;
      this.fetchVehicles();
    },
    resetFilters() {
      this.searchQuery = '';
      this.vehicleTypeFilter = 'all';
      this.assignmentStatusFilter = 'all';
      this.licenseStatusFilter = 'all';
      this.page = 1;
      this.fetchVehicles();
    },
    onPageChange(newPage) {
      if (newPage < 1 || newPage > this.totalPages || newPage === this.page) return;
      this.page = newPage;
      this.fetchVehicles();
    },
    onPerPageChange(newPerPage) {
      this.perPage = Number(newPerPage);
      this.page = 1;
      this.fetchVehicles();
    },

    // Modal 1: Assign Driver
    async openAssignDriverModal(vehicle) {
      this.selectedVehicleForAssign = vehicle;
      this.assignModalOpen = true;
      this.driverSearchQuery = '';
      this.driverFilterTab = 'all';
      await this.fetchAvailableDrivers(vehicle.id);
    },
    async fetchAvailableDrivers(busId) {
      this.loadingAvailableDrivers = true;
      try {
        const res = await axios.get(`/operations/vehicles/${busId}/available-drivers`);
        this.availableDrivers = res.data || [];
      } catch (err) {
        console.error('Failed to load available drivers:', err);
        this.$notify({
          title: 'خطأ',
          text: 'تعذر تحميل قائمة السائقين المتاحين.',
          type: 'error',
        });
      } finally {
        this.loadingAvailableDrivers = false;
      }
    },
    async submitAssignDriver(driverId) {
      if (!this.selectedVehicleForAssign) return;
      this.isSubmittingAction = true;
      this.targetAssignDriverId = driverId;
      try {
        const res = await axios.post(`/operations/vehicles/${this.selectedVehicleForAssign.id}/assign-driver`, {
          driver_id: driverId,
        });
        this.$notify({
          title: 'نجاح',
          text: res.data.message || 'تم تعيين السائق للمركبة بنجاح.',
          type: 'success',
        });
        this.assignModalOpen = false;
        this.fetchVehicles();
        if (this.detailsModalOpen && this.selectedVehicleDetails) {
          this.openDetailsModal(this.selectedVehicleForAssign);
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'تعذر تعيين السائق.';
        this.$notify({
          title: 'خطأ',
          text: msg,
          type: 'error',
        });
      } finally {
        this.isSubmittingAction = false;
        this.targetAssignDriverId = null;
      }
    },
    async submitUnassignDriver() {
      if (!this.selectedVehicleForAssign) return;
      this.isSubmittingAction = true;
      try {
        const res = await axios.post(`/operations/vehicles/${this.selectedVehicleForAssign.id}/unassign-driver`);
        this.$notify({
          title: 'نجاح',
          text: res.data.message || 'تم إلغاء تخصيص السائق بنجاح.',
          type: 'success',
        });
        this.assignModalOpen = false;
        this.fetchVehicles();
        if (this.detailsModalOpen && this.selectedVehicleDetails) {
          this.openDetailsModal(this.selectedVehicleForAssign);
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'تعذر إلغاء تخصيص السائق.';
        this.$notify({
          title: 'خطأ',
          text: msg,
          type: 'error',
        });
      } finally {
        this.isSubmittingAction = false;
      }
    },

    // Modal 2: Vehicle Details & History
    async openDetailsModal(vehicle) {
      this.isLoading = true;
      try {
        const res = await axios.get(`/operations/vehicles/${vehicle.id}`);
        this.selectedVehicleDetails = res.data;
        this.detailsModalOpen = true;
      } catch (err) {
        console.error('Failed to load vehicle details:', err);
        this.$notify({
          title: 'خطأ',
          text: 'تعذر تحميل تفاصيل المركبة.',
          type: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },
    openHistoryDirect(vehicle) {
      this.openDetailsModal(vehicle);
    },
    openAssignFromDetails() {
      if (!this.selectedVehicleDetails || !this.selectedVehicleDetails.bus) return;
      this.openAssignDriverModal(this.selectedVehicleDetails.bus);
    },
    exportCsv(busId) {
      window.open(`/api/operations/vehicles/${busId}/assignment-history/export-csv`, '_blank');
    },

    // Modal 3: Vehicle Types Methods
    async openVehicleTypesModal() {
      this.vehicleTypesModalOpen = true;
      this.resetVehicleTypeForm();
      await this.fetchVehicleTypesFull();
    },
    closeVehicleTypesModal() {
      this.vehicleTypesModalOpen = false;
      this.resetVehicleTypeForm();
    },
    async fetchVehicleTypesFull() {
      this.vehicleTypesLoading = true;
      try {
        const res = await axios.get('/operations/vehicle-types');
        this.vehicleTypesListFull = res.data || [];
      } catch (err) {
        console.error('Failed to load vehicle types:', err);
        this.$notify({
          title: 'خطأ',
          text: 'تعذر تحميل أنواع وسائل النقل.',
          type: 'error',
        });
      } finally {
        this.vehicleTypesLoading = false;
      }
    },
    resetVehicleTypeForm() {
      this.isEditingVehicleType = false;
      this.vehicleTypeFormData = {
        id: null,
        name_ar: '',
        name_en: '',
        seats_count: null,
      };
      this.vehicleTypeFormError = '';
    },
    editVehicleType(t) {
      this.isEditingVehicleType = true;
      this.vehicleTypeFormData = {
        id: t.id,
        name_ar: t.name_ar,
        name_en: t.name_en || '',
        seats_count: t.seats_count,
      };
      this.vehicleTypeFormError = '';
    },
    async saveVehicleType() {
      if (!this.vehicleTypeFormData.name_ar || !this.vehicleTypeFormData.name_ar.trim()) {
        this.vehicleTypeFormError = 'الاسم بالعربية مطلوب.';
        return;
      }
      if (!this.vehicleTypeFormData.seats_count || Number(this.vehicleTypeFormData.seats_count) < 1) {
        this.vehicleTypeFormError = 'عدد المقاعد يجب أن يكون رقماً أكبر من صفر.';
        return;
      }

      this.vehicleTypeFormLoading = true;
      this.vehicleTypeFormError = '';
      try {
        const payload = {
          name_ar: this.vehicleTypeFormData.name_ar.trim(),
          name_en: this.vehicleTypeFormData.name_en ? this.vehicleTypeFormData.name_en.trim() : null,
          seats_count: Number(this.vehicleTypeFormData.seats_count),
        };

        if (this.isEditingVehicleType && this.vehicleTypeFormData.id) {
          const res = await axios.put(`/operations/vehicle-types/${this.vehicleTypeFormData.id}`, payload);
          this.$notify({
            title: 'نجاح',
            text: res.data.message || 'تم تحديث نوع وسيلة النقل بنجاح.',
            type: 'success',
          });
        } else {
          const res = await axios.post('/operations/vehicle-types', payload);
          this.$notify({
            title: 'نجاح',
            text: res.data.message || 'تم إضافة نوع وسيلة النقل بنجاح.',
            type: 'success',
          });
        }

        this.resetVehicleTypeForm();
        await this.fetchVehicleTypesFull();
        this.fetchVehicles();
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'تعذر حفظ نوع وسيلة النقل.';
        this.vehicleTypeFormError = msg;
        this.$notify({
          title: 'خطأ',
          text: msg,
          type: 'error',
        });
      } finally {
        this.vehicleTypeFormLoading = false;
      }
    },
    confirmDeleteVehicleType(t) {
      if (t.buses_count > 0) {
        this.$notify({
          title: 'تنبيه',
          text: `لا يمكن حذف هذا النوع لأنه مرتبط بـ ${t.buses_count} مركبة في النظام.`,
          type: 'warning',
        });
        return;
      }
      this.vehicleTypeToDelete = t;
      this.vehicleTypeDeleteDialog = true;
    },
    async deleteVehicleTypeConfirmed() {
      if (!this.vehicleTypeToDelete) return;
      this.vehicleTypeDeleteLoading = true;
      try {
        const res = await axios.delete(`/operations/vehicle-types/${this.vehicleTypeToDelete.id}`);
        this.$notify({
          title: 'نجاح',
          text: res.data.message || 'تم حذف نوع وسيلة النقل بنجاح.',
          type: 'success',
        });
        this.vehicleTypeDeleteDialog = false;
        this.vehicleTypeToDelete = null;
        if (this.isEditingVehicleType && this.vehicleTypeFormData.id === this.vehicleTypeToDelete?.id) {
          this.resetVehicleTypeForm();
        }
        await this.fetchVehicleTypesFull();
        this.fetchVehicles();
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'تعذر حذف نوع وسيلة النقل.';
        this.$notify({
          title: 'خطأ',
          text: msg,
          type: 'error',
        });
      } finally {
        this.vehicleTypeDeleteLoading = false;
      }
    },

    // Modal 4: Add / Edit Vehicle Methods
    async openAddVehicleModal() {
      if (this.vehicleTypesListFull.length === 0) {
        await this.fetchVehicleTypesFull();
      }
      this.isEditingVehicle = false;
      this.vehicleFormData = {
        id: null,
        vehicle_type_id: null,
        license: '',
        license_expiry_date: '',
        capacity: '',
      };
      this.vehicleFormError = '';
      this.vehicleFormModalOpen = true;
    },
    async openEditVehicleModal(vehicle) {
      if (this.vehicleTypesListFull.length === 0) {
        await this.fetchVehicleTypesFull();
      }
      this.isEditingVehicle = true;
      this.vehicleFormData = {
        id: vehicle.id,
        vehicle_type_id: vehicle.vehicle_type ? vehicle.vehicle_type.id : null,
        license: vehicle.license || '',
        license_expiry_date: vehicle.license_expiry_date || '',
        capacity: vehicle.capacity || '',
      };
      this.vehicleFormError = '';
      this.vehicleFormModalOpen = true;
    },
    onVehicleTypeSelected(typeId) {
      const found = this.vehicleTypesListFull.find((t) => t.id === typeId);
      if (found) {
        this.vehicleFormData.capacity = found.seats_count;
      } else {
        this.vehicleFormData.capacity = '';
      }
    },
    async saveVehicle() {
      if (!this.vehicleFormData.vehicle_type_id) {
        this.vehicleFormError = 'يرجى اختيار نوع وسيلة النقل.';
        return;
      }
      if (!this.vehicleFormData.license || !this.vehicleFormData.license.trim()) {
        this.vehicleFormError = 'لوحة المركبة مطلوبة.';
        return;
      }

      this.vehicleFormLoading = true;
      this.vehicleFormError = '';
      try {
        const payload = {
          vehicle_type_id: this.vehicleFormData.vehicle_type_id,
          license: this.vehicleFormData.license.trim(),
          license_expiry_date: this.vehicleFormData.license_expiry_date || null,
        };

        if (this.isEditingVehicle && this.vehicleFormData.id) {
          const res = await axios.put(`/operations/vehicles/${this.vehicleFormData.id}`, payload);
          this.$notify({
            title: 'نجاح',
            text: res.data.message || 'تم تحديث بيانات المركبة بنجاح.',
            type: 'success',
          });
        } else {
          const res = await axios.post('/operations/vehicles', payload);
          this.$notify({
            title: 'نجاح',
            text: res.data.message || 'تم إضافة وسيلة النقل بنجاح.',
            type: 'success',
          });
        }

        this.vehicleFormModalOpen = false;
        this.fetchVehicles();
        if (this.detailsModalOpen && this.selectedVehicleDetails && this.selectedVehicleDetails.bus.id === this.vehicleFormData.id) {
          this.openDetailsModal({ id: this.vehicleFormData.id });
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'تعذر حفظ بيانات المركبة.';
        this.vehicleFormError = msg;
        this.$notify({
          title: 'خطأ',
          text: msg,
          type: 'error',
        });
      } finally {
        this.vehicleFormLoading = false;
      }
    },

    // Helpers
    getInitials(name) {
      if (!name) return 'س';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + ' ' + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2);
    },
    getAvatarColor(name) {
      const palettes = [
        { bg: '#EEF2FF', text: '#3730A3' },
        { bg: '#F0FDF4', text: '#166534' },
        { bg: '#FEF3C7', text: '#92400E' },
        { bg: '#E0F2FE', text: '#075985' },
        { bg: '#FCE7F3', text: '#9D174D' },
        { bg: '#F3E8FF', text: '#6B21A8' },
        { bg: '#E2E8F0', text: '#1E293B' },
      ];
      if (!name) return palettes[0];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash) % palettes.length;
      return palettes[index];
    },
    getLicenseBadgeStyle(info) {
      const key = info ? info.key : 'unrecorded';
      switch (key) {
        case 'valid':
          return {
            backgroundColor: '#DCFCE7',
            color: '#15803D',
            borderColor: '#BBF7D0',
          };
        case 'near_expiry':
          return {
            backgroundColor: '#FFF3D6',
            color: '#C56A00',
            borderColor: '#FED7AA',
          };
        case 'expired':
          return {
            backgroundColor: '#FEE2E2',
            color: '#DC2626',
            borderColor: '#FECACA',
          };
        default:
          return {
            backgroundColor: '#EEF1F5',
            color: '#596579',
            borderColor: '#CBD5E1',
          };
      }
    },
    getLicenseIcon(key) {
      switch (key) {
        case 'valid':
          return 'mdi-check-circle';
        case 'near_expiry':
          return 'mdi-clock-alert-outline';
        case 'expired':
          return 'mdi-alert-circle-outline';
        default:
          return 'mdi-help-circle-outline';
      }
    },
  },
};
</script>

<style scoped>
.operations-container {
  width: 100%;
  max-width: 1650px;
  margin: 0 auto;
  padding: 24px 32px;
  background-color: #F5F7FA;
  min-height: 100vh;
  font-family: 'Cairo', sans-serif;
  color: #142B4A;
}

/* Header */
.op-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.op-main-title {
  font-size: 26px;
  font-weight: 800;
  color: #082C69;
  margin: 0;
  line-height: 1.2;
}

.op-scope-pill {
  padding: 4px 12px;
  background: #E0F2FE;
  border: 1px solid #BAE6FD;
  border-radius: 20px;
  color: #0369A1;
  font-size: 12.5px;
}

.op-subtitle {
  font-size: 14px;
  color: #63738A;
  margin: 4px 0 0 0;
  font-weight: 500;
}

.op-btn-primary {
  background-color: #082C69 !important;
  color: #FFFFFF !important;
  border: 1px solid #082C69 !important;
  border-radius: 10px !important;
  height: 42px !important;
  padding: 0 16px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
}

.op-btn-primary:hover:not(:disabled) {
  background-color: #0c3984 !important;
  border-color: #0c3984 !important;
  box-shadow: 0 2px 6px rgba(8, 44, 105, 0.2) !important;
}

.op-btn-primary:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

.op-btn-secondary {
  background-color: #EEF2FF !important;
  color: #082C69 !important;
  border: 1px solid #C7D2FE !important;
  border-radius: 10px !important;
  height: 42px !important;
  padding: 0 16px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
}

.op-btn-secondary:hover:not(:disabled) {
  background-color: #E0E7FF !important;
  border-color: #A5B4FC !important;
}

.op-form-input {
  width: 100%;
  height: 42px;
  border: 1px solid #DFE5EE;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13.5px;
  color: #082C69;
  background-color: #FFFFFF;
  transition: all 0.2s ease;
  outline: none;
}

.op-form-input:focus {
  border-color: #082C69;
  box-shadow: 0 0 0 2px rgba(8, 44, 105, 0.1);
}

.op-input-readonly {
  background-color: #F8FAFC !important;
  color: #64748B !important;
  cursor: not-allowed;
}

.form-label-bold {
  font-size: 13px;
  font-weight: 700;
  color: #082C69;
  margin-bottom: 4px;
}

.op-btn-form-cancel {
  height: 42px;
  padding: 0 16px;
  border: 1px solid #DFE5EE;
  border-radius: 8px;
  background-color: #FFFFFF;
  color: #64748B;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.op-btn-form-cancel:hover {
  background-color: #F1F5F9;
  color: #334155;
}

.op-seat-badge {
  background-color: #F1F5F9;
  color: #082C69;
  padding: 3px 10px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 12px;
  display: inline-block;
}

.op-btn-refresh {
  background-color: #FFFFFF !important;
  border: 1px solid #E1E7EF !important;
  border-radius: 10px !important;
  height: 42px !important;
  padding: 0 16px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  color: #082C69 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
}

.op-btn-refresh:hover:not(:disabled) {
  background-color: #F8FAFC !important;
  border-color: #082C69 !important;
}

/* 6 KPI Cards Grid */
.op-kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 14px;
}

@media (max-width: 1400px) {
  .op-kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .op-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.op-kpi-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 94px;
}

.op-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(8, 44, 105, 0.06);
}

.kpi-label {
  font-size: 13px;
  font-weight: 600;
  color: #63738A;
  margin-bottom: 3px;
}

.kpi-value {
  font-size: 26px;
  font-weight: 800;
  line-height: 1.1;
}

.kpi-icon-side {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Filter Bar */
.op-filter-bar {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.filter-controls-group {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-grow: 1;
  flex-wrap: wrap;
}

.filter-item-wrapper {
  display: flex;
  flex-direction: column;
  min-width: 170px;
  flex: 1;
}

.filter-item-wrapper.search-wrapper {
  flex: 2;
  min-width: 240px;
}

.filter-label {
  font-size: 12.5px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 6px;
}

.op-search-input-box {
  display: flex;
  align-items: center;
  height: 40px;
  border: 1px solid #DFE5EE;
  border-radius: 8px;
  padding: 0 12px;
  background: #FFFFFF;
  transition: border-color 0.2s ease;
}

.op-search-input-box:focus-within {
  border-color: #082C69;
}

.op-native-input {
  border: none;
  outline: none;
  font-size: 13.5px;
  width: 100%;
  color: #142B4A;
  font-family: 'Cairo', sans-serif;
}

.clear-search-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
}

::v-deep .op-custom-select .v-input__control {
  min-height: 40px !important;
}

::v-deep .op-custom-select .v-input__slot {
  min-height: 40px !important;
  border-color: #DFE5EE !important;
  border-radius: 8px !important;
  background-color: #FFFFFF !important;
}

::v-deep .op-custom-select.v-input--is-focused .v-input__slot {
  border-color: #082C69 !important;
}

::v-deep .op-custom-select .v-select__selection {
  font-size: 13.5px !important;
  color: #142B4A !important;
  font-weight: 600 !important;
}

.filter-actions-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.op-filter-btn {
  height: 40px;
  padding: 0 14px;
  background: #FFFFFF;
  border: 1px solid #DFE5EE;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 700;
  color: #63738A;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.op-filter-btn:hover {
  border-color: #082C69;
  color: #082C69;
  background-color: #F8FAFC;
}

/* Table Card */
.op-table-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.op-plate-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #FFFFFF;
  border: 2px solid #082C69;
  border-radius: 6px;
  font-weight: 800;
  font-size: 13.5px;
  color: #082C69;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 2px rgba(8, 44, 105, 0.1);
}

.driver-avatar-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 13.5px;
  flex-shrink: 0;
  overflow: hidden;
}

.driver-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.driver-name-text {
  font-weight: 700;
  color: #082C69;
  font-size: 14px;
  line-height: 1.2;
}

.vehicle-unlinked-text {
  color: #94A3B8;
  font-size: 13px;
  font-weight: 600;
}

.op-status-badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
}

.op-row-action-btn {
  width: 32px;
  height: 32px;
  background: #FFFFFF;
  border: 1px solid #DDE3EC;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.op-row-action-btn:hover {
  border-color: #082C69;
  background: #F8FAFC;
}

/* Pagination */
.op-pagination-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-top: 1px solid #E1E7EF;
  background: #FFFFFF;
  flex-wrap: wrap;
  gap: 12px;
}

.pagination-label {
  font-size: 13.5px;
  color: #63738A;
  font-weight: 600;
}

.op-rows-select {
  height: 34px;
  padding: 0 10px;
  border: 1px solid #DFE5EE;
  border-radius: 6px;
  background-color: #FFFFFF;
  font-size: 13.5px;
  font-weight: 600;
  color: #142B4A;
  cursor: pointer;
}

.pagination-info {
  font-size: 13.5px;
  font-weight: 600;
  color: #63738A;
}

.op-page-nav-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #DFE5EE;
  border-radius: 6px;
  background: #FFFFFF;
  color: #63738A;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.op-page-nav-btn:hover:not(:disabled) {
  border-color: #082C69;
  color: #082C69;
  background: #F8FAFC;
}

.op-page-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.op-page-number-btn {
  min-width: 34px;
  height: 34px;
  padding: 0 8px;
  border: 1px solid #DFE5EE;
  border-radius: 6px;
  background: #FFFFFF;
  color: #63738A;
  font-size: 13.5px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.op-page-number-btn:hover {
  background: #F8FAFC;
  border-color: #CBD5E1;
  color: #142B4A;
}

.op-page-number-btn.active {
  background: #082C69;
  border-color: #082C69;
  color: #FFFFFF;
  font-weight: 700;
}

.op-page-ellipsis {
  padding: 0 4px;
  color: #94A3B8;
  font-weight: 700;
}

/* Modals */
::v-deep .op-details-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 20px 40px -10px rgba(8, 44, 105, 0.25) !important;
}

.op-dialog-card {
  border-radius: 14px !important;
  overflow: hidden;
  background: #FFFFFF;
}

.op-dialog-header {
  border-bottom: 1px solid #E1E7EF;
  background: #FFFFFF;
}

.op-dialog-footer {
  border-top: 1px solid #E1E7EF;
  background: #FFFFFF;
}

.op-modal-scroll-body {
  background-color: #F8FAFC;
}

.vehicle-summary-banner {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.summary-cell {
  display: flex;
  flex-direction: column;
}

.op-info-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.op-card-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #F1F5F9;
}

.op-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #082C69;
}

.op-sub-kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

@media (max-width: 960px) {
  .op-sub-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.op-sub-kpi-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.detail-label {
  font-size: 12.5px;
  color: #63738A;
  margin-bottom: 2px;
  font-weight: 500;
}

.meta-spec-item {
  background: #F8FAFC;
  border: 1px solid #E1E7EF;
  border-radius: 8px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  font-size: 13px;
}

.spec-label {
  color: #63738A;
  margin-inline-end: 6px;
  font-weight: 500;
}

.spec-val {
  font-weight: 700;
}

.op-modal-table {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #E1E7EF;
  background: #FFFFFF;
}

.op-modal-table th {
  background-color: #F8FAFC !important;
  color: #63738A !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  white-space: nowrap;
}

.op-modal-table td {
  font-size: 13px !important;
  color: #142B4A;
}

/* Available drivers in assign modal */
.op-filter-chip-btn {
  height: 32px;
  padding: 0 12px;
  border: 1px solid #DFE5EE;
  border-radius: 6px;
  background: #FFFFFF;
  font-size: 12.5px;
  font-weight: 600;
  color: #63738A;
  cursor: pointer;
  transition: all 0.2s ease;
}

.op-filter-chip-btn.active {
  background-color: #082C69;
  border-color: #082C69;
  color: #FFFFFF;
}

.available-drivers-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.driver-selection-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.driver-selection-row:hover {
  background: #F1F5F9;
  border-color: #CBD5E1;
}

.driver-selection-row.row-conflict {
  background: #FFFBEB;
  border-color: #FDE68A;
}

.driver-selection-row.row-current {
  background: #F0FDF4;
  border-color: #BBF7D0;
}

.badge-current-assigned {
  background: #DCFCE7;
  color: #15803D;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
}

/* Semantic Colors */
.text-navy {
  color: #082C69 !important;
}

.text-emerald {
  color: #15803D !important;
}

.text-blue {
  color: #0284C7 !important;
}

.text-amber {
  color: #D97706 !important;
}

.text-rose {
  color: #DC2626 !important;
}

.text-muted {
  color: #63738A !important;
}
</style>
